// go-chat-duplication
import { ChangeDetectionStrategy, Component, OnInit, inject, DestroyRef, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { map, take } from 'rxjs/operators';

import { GoNotificationsService, GoNotificationStateService, GoAudioService } from '@shared/services/go-notifications';
import { GoNotificationState } from '@shared/models/go-notification-state.model';
import { MessageService } from '@app/modules/shared/services/message.service';
import { GoActiveChatSessionsService } from '@app/modules/therapist/go-chat/go-active-chat-sessions.service';
import { AssignedPatientsService } from '@app/modules/therapist/assigned-patients/assigned-patients.service';
import { GoDatabaseNotificationType } from '@shared/services/go-notifications/go-database-notification-type';
import { MatIconButton } from '@angular/material/button';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { MatBadge } from '@angular/material/badge';
import { GoPrimaryNotificationComponent } from './go-primary-notification/go-primary-notification.component';
import { GoChatRequestNotificationDirective } from './go-chat-request-notification.directive';
import { GoChatUpgradeNotificationDirective } from './go-chat-upgrade-notification.directive';
import { GoAssignedPatientNotificationDirective } from './go-assigned-patient-notification.directive';
import { MatDivider } from '@angular/material/divider';

@Component({
  selector: 'app-go-primary-notification-button',
  templateUrl: './go-primary-notification-button.component.html',
  styleUrl: './go-primary-notification-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconButton,
    MatMenuTrigger,
    MatIcon,
    MatBadge,
    MatMenu,
    MatMenuItem,
    GoPrimaryNotificationComponent,
    GoChatRequestNotificationDirective,
    GoChatUpgradeNotificationDirective,
    GoAssignedPatientNotificationDirective,
    MatDivider,
  ],
})
export class GoPrimaryNotificationButtonComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private notificationsService = inject(GoNotificationsService);
  private notificationStateService = inject(GoNotificationStateService);
  private assignedPatientsService = inject(AssignedPatientsService);
  private goActiveChatSessionsService = inject(GoActiveChatSessionsService);
  private audioService = inject(GoAudioService);
  private messageService = inject(MessageService);

  public readonly notifications = signal<GoDatabaseNotificationType[]>([]);
  public readonly unreadNotificationsCount = computed(
    () => this.notifications().filter((notification) => notification.read === null).length
  );

  ngOnInit() {
    const notificationObservable = this.notificationsService
      .getNotificationObservable()
      .pipe(takeUntilDestroyed(this.destroyRef));

    notificationObservable
      .pipe(
        map((notifications) => {
          return notifications.filter((notifications) => notifications.data['pubNub'] === 'true');
        })
      )
      .subscribe((databaseNotifications) => {
        this.notifications.set(databaseNotifications);
        if (localStorage.getItem('soundNotificationsEnabled') !== 'false') {
          const unreadChatRequests = this.notifications().filter(
            (notification) =>
              (notification.data.type === 'chatRequest' || notification.data.type === 'chatUpgraded') &&
              notification.read === null
          );
          const chatUpgraded = unreadChatRequests.filter((notification) => notification.data.type === 'chatUpgraded');
          let alreadyNotified = [];
          this.notificationStateService.notifiedUserRequests.pipe(take(1)).subscribe((n) => (alreadyNotified = n));

          if (chatUpgraded.length > 0) {
            for (const chat of chatUpgraded) {
              const userNotifiedOfUpgrade = alreadyNotified.filter((n) => n.id === chat.id)[0];

              if (!userNotifiedOfUpgrade || !userNotifiedOfUpgrade.userNotified) {
                void this.goActiveChatSessionsService.clearCache();
                this.assignedPatientsService.dataStateValid$.next(false);
              }
            }
          }

          if (unreadChatRequests.length > 0) {
            let unreadRequestArray = this.unreadRequests(unreadChatRequests, alreadyNotified);

            if (unreadRequestArray.length > 0 && alreadyNotified.length === 0) {
              void this.audioService.playAudioChatRequest();
              unreadRequestArray = this.notificationStateService.setAllUserNotified(unreadRequestArray);
            } else {
              for (const unreadRequest of unreadRequestArray) {
                const notified = alreadyNotified.filter((n) => n.id === unreadRequest.id);

                if (notified.length === 0 && unreadRequest.unread === 1 && !unreadRequest.userNotified) {
                  void this.audioService.playAudioChatRequest();
                  unreadRequestArray = this.notificationStateService.setUserNotified(
                    unreadRequest.id,
                    unreadRequestArray
                  );
                }
              }
            }

            this.notificationStateService.notifiedUserRequests.next(unreadRequestArray);
          }
        }
      });
  }

  public async markAllChatRequestNotificationsAsRead(): Promise<void> {
    const unreadNotifications = this.notifications().filter((notification) => notification.read === null);
    const unreadNotificationIds = unreadNotifications.map((notification) => notification.id);
    try {
      await this.notificationStateService.markAllChatRequestNotificationsAsRead(unreadNotificationIds);
    } catch {
      this.messageService.showMessage('Error: One or more notifications could not be cleared');
    }
  }

  private unreadRequests(
    unreadChatRequests: GoDatabaseNotificationType[],
    alreadyNotified: GoNotificationState[]
  ): GoNotificationState[] {
    const addToNotified: GoNotificationState[] = [];
    let notified = null;

    for (const unreadRequest of unreadChatRequests) {
      if (alreadyNotified) {
        notified = alreadyNotified.filter((userNotified) => userNotified.id === unreadRequest.id)[0];
      }

      const unread = {
        id: unreadRequest.id,
        unread: 1,
        userNotified: notified?.userNotified || false,
      };

      addToNotified.push(unread);
    }

    return addToNotified;
  }
}
